import { Navigate, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { useSession } from '@/api/session';
import { RootLayout } from '@/components/layout/RootLayout';
import { BoardPage } from '@/routes/BoardPage';
import { OrdersPage } from '@/routes/OrdersPage';
import { OrderDetailPage } from '@/routes/OrderDetailPage';
import { OrderFormPage } from '@/routes/OrderFormPage';
import { QuotePage } from '@/routes/QuotePage';
import { TripsPage } from '@/routes/TripsPage';
import { TripDetailPage } from '@/routes/TripDetailPage';
import { MapPage } from '@/routes/MapPage';
import { DocumentsPage } from '@/routes/DocumentsPage';
import { PartnersPage } from '@/routes/PartnersPage';
import { VehiclesPage } from '@/routes/VehiclesPage';
import { VanPage } from '@/routes/VanPage';
import { DriversPage } from '@/routes/DriversPage';
import { ReportsPage } from '@/routes/ReportsPage';
import { DuesPage } from '@/routes/DuesPage';
import { FuelPage } from '@/routes/FuelPage';
import { IntegrationsPage } from '@/routes/IntegrationsPage';
import { ServicePage } from '@/routes/ServicePage';
import { AnomaliesPage } from '@/routes/AnomaliesPage';
import { VatPage } from '@/routes/VatPage';
import { VignettesPage } from '@/routes/VignettesPage';
import { DriverPage } from '@/routes/DriverPage';
import { UsersPage } from '@/routes/UsersPage';
import { EmptyState } from '@/components/ui/State';

/**
 * Маршрутите се описват в код, а не по файлове: единадесет екрана се четат
 * по-лесно в един списък, отколкото в дърво от папки, а и не изисква
 * допълнителен плъгин за генериране.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: () => (
    <EmptyState title="Страницата не съществува" body="Проверете адреса или се върнете на борда." />
  ),
});

interface TextSearch {
  q?: string;
}

const searchWithText = (search: Record<string, unknown>): TextSearch => ({
  q: typeof search.q === 'string' ? search.q : undefined,
});

/**
 * „/" е бордът — но за счетоводителя, който не разпределя бусове и няма
 * достъп до борда, началото са падежите. Пренасочване, не празен екран с грешка.
 */
function HomeRoute() {
  const role = useSession((s) => s.user?.role);
  if (role === 'accountant') return <Navigate to="/dues" replace />;
  if (role === 'mechanic') return <Navigate to="/service" replace />;
  return <BoardPage />;
}

const boardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomeRoute });

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
  validateSearch: (search: Record<string, unknown>): TextSearch & { group?: string } => ({
    ...searchWithText(search),
    group: typeof search.group === 'string' ? search.group : undefined,
  }),
});

const orderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/new',
  component: OrderFormPage,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$orderId',
  component: OrderDetailPage,
});

const orderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$orderId/edit',
  component: OrderFormPage,
});

const quoteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/quote', component: QuotePage });

const tripsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/trips', component: TripsPage });

const tripDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trips/$tripId',
  component: TripDetailPage,
});

const mapRoute = createRoute({ getParentRoute: () => rootRoute, path: '/map', component: MapPage });

const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  component: DocumentsPage,
});

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners',
  component: PartnersPage,
  validateSearch: (search: Record<string, unknown>): TextSearch => searchWithText(search),
});


const vehiclesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vehicles',
  component: VehiclesPage,
  /** `?edit=<id>` отваря формата на буса — от страницата на буса „Данни на буса". */
  validateSearch: (search: Record<string, unknown>): { edit?: string } =>
    typeof search.edit === 'string' ? { edit: search.edit } : {},
});

const vanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vehicles/$vehicleId',
  component: VanPage,
});

const driversRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/drivers',
  component: DriversPage,
  /** `?edit=<id>` отваря формата на шофьора — от досието му „Данни на шофьора". */
  validateSearch: (search: Record<string, unknown>): { edit?: string } =>
    typeof search.edit === 'string' ? { edit: search.edit } : {},
});
const driverRoute = createRoute({ getParentRoute: () => rootRoute, path: '/drivers/$driverId', component: DriverPage });

const REPORT_TAB_KEYS = ['trips', 'customers', 'drivers', 'vans'] as const;
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
  /** `?tab=trips` — разделът се пази в адреса, за да се праща като линк. */
  validateSearch: (search: Record<string, unknown>): { tab?: (typeof REPORT_TAB_KEYS)[number] } =>
    REPORT_TAB_KEYS.includes(search.tab as (typeof REPORT_TAB_KEYS)[number])
      ? { tab: search.tab as (typeof REPORT_TAB_KEYS)[number] }
      : {},
});

const duesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dues', component: DuesPage });
const fuelRoute = createRoute({ getParentRoute: () => rootRoute, path: '/fuel', component: FuelPage });
const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations',
  component: IntegrationsPage,
});
const usersRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/users', component: UsersPage });
const anomaliesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/anomalies', component: AnomaliesPage });
const vatRoute = createRoute({ getParentRoute: () => rootRoute, path: '/vat', component: VatPage });
const vignettesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/vignettes', component: VignettesPage });

const SERVICE_TABS = ['vans', 'signals', 'repairs', 'shopping'] as const;
export type ServiceTab = (typeof SERVICE_TABS)[number];
const serviceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service',
  component: ServicePage,
  validateSearch: (search: Record<string, unknown>): { tab?: ServiceTab; repair?: string } => ({
    tab: SERVICE_TABS.includes(search.tab as ServiceTab) ? (search.tab as ServiceTab) : undefined,
    repair: typeof search.repair === 'string' ? search.repair : undefined,
  }),
});

const routeTree = rootRoute.addChildren([
  boardRoute,
  ordersRoute,
  orderNewRoute,
  orderDetailRoute,
  orderEditRoute,
  quoteRoute,
  tripsRoute,
  tripDetailRoute,
  mapRoute,
  documentsRoute,
  partnersRoute,
  vehiclesRoute,
  vanRoute,
  driversRoute,
  driverRoute,
  reportsRoute,
  duesRoute,
  fuelRoute,
  integrationsRoute,
  usersRoute,
  serviceRoute,
  anomaliesRoute,
  vatRoute,
  vignettesRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 120,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
